<script lang="ts" setup>
import { onBeforeMount, computed, watch, ref } from 'vue'

import { useConfigStore } from '@toni/rendering/src/stores/config'
import { useSessionStore } from '@toni/rendering/src/stores/session'
import { useSourcepointStore } from '@toni/rendering/src/stores/sourcepoint'
import { useDataLayer } from '@toni/brand-ui'

import { useClient } from '../../utils/client-only'

const gtmId = useConfigStore().rsConfig.googleTagManagerId as string
const gaId = useConfigStore().rsConfig.consent.vendor.googleanalytics as string

const scriptInjected = ref(false)
const { gtag } = useDataLayer()

const { isClient } = useClient()

const sessionStore = useSessionStore()
const sourcepointStore = useSourcepointStore()
const simulateContentPassOrContentPass = computed(() => sessionStore.simulateContentPassOrContentPass)
const consentedVendors = computed(() => sourcepointStore.consentedVendors)

const gaHasConsent = computed(() => {
  return consentedVendors.value.includes(gaId)
})
// Only load GTM after consent and if no PUR sub exists & do not use google tag manager on development
const shouldLoadGTM = computed(() => {
  return !simulateContentPassOrContentPass.value &&
         gaHasConsent.value
})

type GtagArgs = {
  ad_storage?: 'granted' | 'denied',
  analytics_storage?: 'granted' | 'denied',
  functionality_storage?: 'granted' | 'denied',
  personalization_storage?: 'granted' | 'denied',
  security_storage?: 'granted' | 'denied',
  ad_user_data?: 'granted' | 'denied',
  ad_personalization?: 'granted' | 'denied'
}

const injectGTM = () => {
  if (shouldLoadGTM.value) {
    gtag('consent', 'update', {
      ad_storage: 'granted',
      analytics_storage: 'granted',
      functionality_storage: 'granted',
      personalization_storage: 'granted',
      security_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted'
    } satisfies GtagArgs)

    if (!scriptInjected.value) {
      const script = document.createElement('script')
      script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmId}');`
      document.head.appendChild(script)

      scriptInjected.value = true
    }
    gtag('config', gtmId)
  } else {
    // if should not load gtm but we already did (e.g. user removed consent in sourcepoint) we update the datalayer and remove the script
    if (scriptInjected.value) {
      // tell datalayer to remove consent
      gtag('consent', 'update', {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      } satisfies GtagArgs)
      // remove gtm script
      const script = document.querySelector('script[src*="googletagmanager"]')
      if (script) script.remove()
      // revert render flag
      scriptInjected.value = false
    }
  }
}

watch(shouldLoadGTM, (newVal) => {
  if (newVal) {
    injectGTM()
  }
})

onBeforeMount(() => {
  gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  } satisfies GtagArgs)
  if (shouldLoadGTM.value) {
    injectGTM()
  }
})
</script>

<template>
  <teleport v-if="isClient && shouldLoadGTM && gaHasConsent.value"
            to="body">
    <noscript>
      <iframe
      :src="`https://www.googletagmanager.com/ns.html?id=${gtmId}`"
      height="0"
      width="0"
      style="display:none;visibility:hidden"
      />
    </noscript>
  </teleport>
</template>
